// src/index.ts
var DomainError = class extends Error {
  constructor(message, code, statusCode = 400) {
    super(message);
    this.code = code;
    this.statusCode = statusCode;
    this.name = "DomainError";
  }
  code;
  statusCode;
};
function assertVnd(value, field = "amount") {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new DomainError(`${field} ph\u1EA3i l\xE0 s\u1ED1 nguy\xEAn VND kh\xF4ng \xE2m`, "INVALID_MONEY");
  }
  return value;
}
function roundQuantity(value) {
  if (!Number.isFinite(value)) {
    throw new DomainError("S\u1ED1 l\u01B0\u1EE3ng kh\xF4ng h\u1EE3p l\u1EC7", "INVALID_QUANTITY");
  }
  return Math.round((value + Number.EPSILON) * 1e3) / 1e3;
}
function lineTotal(unitPrice, quantity) {
  assertVnd(unitPrice, "unitPrice");
  if (!Number.isFinite(quantity) || quantity <= 0) {
    throw new DomainError("S\u1ED1 l\u01B0\u1EE3ng ph\u1EA3i l\u1EDBn h\u01A1n 0", "INVALID_QUANTITY");
  }
  return Math.round(unitPrice * quantity);
}
function convertToBaseQuantity(quantity, multiplier = 1) {
  if (!Number.isFinite(quantity) || quantity <= 0) {
    throw new DomainError("S\u1ED1 l\u01B0\u1EE3ng ph\u1EA3i l\u1EDBn h\u01A1n 0", "INVALID_QUANTITY");
  }
  if (!Number.isFinite(multiplier) || multiplier <= 0) {
    throw new DomainError("H\u1EC7 s\u1ED1 quy \u0111\u1ED5i ph\u1EA3i l\u1EDBn h\u01A1n 0", "INVALID_UNIT_MULTIPLIER");
  }
  const result = roundQuantity(quantity * multiplier);
  if (result <= 0) {
    throw new DomainError("S\u1ED1 l\u01B0\u1EE3ng sau quy \u0111\u1ED5i kh\xF4ng h\u1EE3p l\u1EC7", "INVALID_QUANTITY");
  }
  return result;
}
function baseUnitCost(unitCost, multiplier = 1) {
  assertVnd(unitCost, "unitCost");
  if (!Number.isFinite(multiplier) || multiplier <= 0) {
    throw new DomainError("H\u1EC7 s\u1ED1 quy \u0111\u1ED5i ph\u1EA3i l\u1EDBn h\u01A1n 0", "INVALID_UNIT_MULTIPLIER");
  }
  return Math.round(unitCost / multiplier);
}
function unitPriceFromBase(basePrice, multiplier = 1) {
  assertVnd(basePrice, "basePrice");
  if (!Number.isFinite(multiplier) || multiplier <= 0) {
    throw new DomainError("H\u1EC7 s\u1ED1 quy \u0111\u1ED5i ph\u1EA3i l\u1EDBn h\u01A1n 0", "INVALID_UNIT_MULTIPLIER");
  }
  return Math.round(basePrice * multiplier);
}
function calculateWeightedAverageCost(currentQty, currentAvgCost, receivedQty, receivedUnitCost) {
  if (receivedQty <= 0) {
    throw new DomainError("S\u1ED1 l\u01B0\u1EE3ng nh\u1EADp ph\u1EA3i l\u1EDBn h\u01A1n 0 \u0111\u1EC3 t\xEDnh gi\xE1 v\u1ED1n", "INVALID_QUANTITY");
  }
  assertVnd(receivedUnitCost, "receivedUnitCost");
  if (currentQty <= 0) {
    return Math.round(receivedUnitCost);
  }
  assertVnd(Math.round(currentAvgCost), "currentAvgCost");
  const nextQty = currentQty + receivedQty;
  if (nextQty <= 0) return 0;
  return Math.round(
    (currentQty * currentAvgCost + receivedQty * receivedUnitCost) / nextQty
  );
}
var weightedAverageCost = calculateWeightedAverageCost;
function calculateSaleCogs(baseQuantity, unitCost) {
  assertVnd(unitCost, "unitCost");
  if (!Number.isFinite(baseQuantity) || baseQuantity < 0) {
    throw new DomainError("S\u1ED1 l\u01B0\u1EE3ng kh\xF4ng h\u1EE3p l\u1EC7", "INVALID_QUANTITY");
  }
  return Math.round(baseQuantity * unitCost);
}
function calculateNetSales(grossSales, discounts = 0, returns = 0) {
  assertVnd(grossSales, "grossSales");
  assertVnd(discounts, "discounts");
  assertVnd(returns, "returns");
  return Math.max(0, grossSales - discounts - returns);
}
function calculateGrossProfit(netSales, cogs) {
  return netSales - cogs;
}
function calculateGrossMargin(netSales, grossProfit) {
  if (netSales <= 0) return 0;
  return Math.round(grossProfit / netSales * 1e4) / 100;
}
function calculateSellableStock(params) {
  const onHand = roundQuantity(params.onHand);
  const reserved = roundQuantity(Math.max(0, params.reserved ?? 0));
  const blocked = roundQuantity(Math.max(0, params.blocked ?? 0));
  const available = roundQuantity(Math.max(0, onHand - reserved - blocked));
  return { onHand, reserved, blocked, available };
}
function toBusinessDateString(date) {
  if (typeof date === "string") {
    const match = date.match(/^\d{4}-\d{2}-\d{2}/);
    if (match) return match[0];
  }
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function isDateExpired(expiryDate, referenceDate = /* @__PURE__ */ new Date()) {
  if (!expiryDate) return false;
  const expStr = toBusinessDateString(expiryDate);
  const refStr = toBusinessDateString(referenceDate);
  return refStr > expStr;
}
function ensureStockAvailable(currentQty, requestedQty, allowNegative = false) {
  if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
    throw new DomainError("S\u1ED1 l\u01B0\u1EE3ng b\xE1n ph\u1EA3i l\u1EDBn h\u01A1n 0", "INVALID_QUANTITY");
  }
  if (!allowNegative && currentQty + 5e-4 < requestedQty) {
    throw new DomainError(
      "T\u1ED3n kho kh\xF4ng \u0111\u1EE7 \u0111\u1EC3 ho\xE0n t\u1EA5t giao d\u1ECBch",
      "INSUFFICIENT_STOCK",
      409
    );
  }
}
function calculateCashChange(total, tendered) {
  assertVnd(total, "total");
  assertVnd(tendered, "tendered");
  if (tendered < total) {
    throw new DomainError(
      "S\u1ED1 ti\u1EC1n kh\xE1ch \u0111\u01B0a ch\u01B0a \u0111\u1EE7",
      "INSUFFICIENT_CASH"
    );
  }
  return tendered - total;
}
function reconcileClosing(systemCash, actualCash) {
  assertVnd(systemCash, "systemCash");
  assertVnd(actualCash, "actualCash");
  const discrepancy = actualCash - systemCash;
  return {
    systemCash,
    actualCash,
    discrepancy,
    requiresReason: discrepancy !== 0
  };
}
function normalizeQuantity(value) {
  if (!Number.isFinite(value) || value === 0) {
    throw new DomainError(
      "S\u1ED1 l\u01B0\u1EE3ng \u0111i\u1EC1u ch\u1EC9nh kh\xF4ng h\u1EE3p l\u1EC7",
      "INVALID_QUANTITY"
    );
  }
  return roundQuantity(value);
}
function categorizeExpiry(expiryDate, referenceDate = /* @__PURE__ */ new Date()) {
  if (!expiryDate) {
    return { severity: "NORMAL", daysRemaining: null };
  }
  const exp = typeof expiryDate === "string" ? new Date(expiryDate) : expiryDate;
  const ref = new Date(referenceDate);
  const expDay = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate()).getTime();
  const refDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime();
  const diffDays = Math.round((expDay - refDay) / (24 * 60 * 60 * 1e3));
  if (diffDays < 0) {
    return { severity: "EXPIRED", daysRemaining: diffDays };
  }
  if (diffDays <= 7) {
    return { severity: "CRITICAL", daysRemaining: diffDays };
  }
  if (diffDays <= 30) {
    return { severity: "WARNING", daysRemaining: diffDays };
  }
  if (diffDays <= 60) {
    return { severity: "ATTENTION", daysRemaining: diffDays };
  }
  return { severity: "NORMAL", daysRemaining: diffDays };
}
function allocateFefo(lots, requestedBaseQuantity, options) {
  const requested = convertToBaseQuantity(requestedBaseQuantity, 1);
  let remaining = requested;
  const validLots = lots.filter((lot) => {
    if (lot.quantity <= 0) return false;
    if (options?.referenceDate != null && !options.allowExpired && lot.expiryDate) {
      if (isDateExpired(lot.expiryDate, options.referenceDate)) {
        return false;
      }
    }
    return true;
  });
  const sorted = [...validLots].sort((a, b) => {
    const at = a.expiryDate ? new Date(a.expiryDate).getTime() : Number.MAX_SAFE_INTEGER;
    const bt = b.expiryDate ? new Date(b.expiryDate).getTime() : Number.MAX_SAFE_INTEGER;
    if (at !== bt) return at - bt;
    return a.id.localeCompare(b.id);
  });
  const allocations = [];
  for (const lot of sorted) {
    if (remaining <= 5e-4) break;
    const available = roundQuantity(lot.quantity);
    const take = roundQuantity(Math.min(available, remaining));
    if (take <= 0) continue;
    allocations.push({ lotId: lot.id, baseQuantity: take });
    remaining = roundQuantity(remaining - take);
  }
  if (remaining > 5e-4) {
    throw new DomainError(
      "T\u1ED3n theo l\xF4 kh\xF4ng \u0111\u1EE7 \u0111\u1EC3 ho\xE0n t\u1EA5t giao d\u1ECBch (c\xE1c l\xF4 c\xF2n l\u1EA1i \u0111\xE3 h\u1EBFt h\u1EA1n ho\u1EB7c kh\xF4ng \u0111\u1EE7)",
      "INSUFFICIENT_LOT_STOCK",
      409
    );
  }
  return allocations;
}
function calculateReturnAmount(unitPrice, returnQuantity) {
  return lineTotal(unitPrice, returnQuantity);
}
function validateTenantStatusTransition(from, to) {
  if (from === to) return;
  const normalize = (s) => s === "CANCELLED" ? "CANCELED" : s;
  const normFrom = normalize(from);
  const normTo = normalize(to);
  const allowed = {
    PROVISIONING: ["ACTIVE", "CANCELED"],
    ACTIVE: ["READ_ONLY", "SUSPENDED", "CANCELED"],
    READ_ONLY: ["ACTIVE", "SUSPENDED", "CANCELED"],
    SUSPENDED: ["ACTIVE", "READ_ONLY", "CANCELED", "ARCHIVED"],
    CANCELED: ["ARCHIVED"],
    ARCHIVED: []
  };
  if (!allowed[normFrom]?.includes(normTo)) {
    throw new DomainError(
      `Chuy\u1EC3n \u0111\u1ED5i tr\u1EA1ng th\xE1i t\u1ED5 ch\u1EE9c kh\xF4ng h\u1EE3p l\u1EC7 t\u1EEB ${from} sang ${to}`,
      "INVALID_TENANT_STATUS_TRANSITION",
      400
    );
  }
}
function validateSubscriptionStatusTransition(from, to) {
  if (from === to) return;
  const normalize = (s) => s === "CANCELLED" ? "CANCELED" : s;
  const normFrom = normalize(from);
  const normTo = normalize(to);
  const allowed = {
    TRIAL: ["ACTIVE", "PAST_DUE", "GRACE_PERIOD", "READ_ONLY", "CANCELED"],
    ACTIVE: ["PAST_DUE", "GRACE_PERIOD", "READ_ONLY", "SUSPENDED", "CANCELED"],
    PAST_DUE: ["GRACE_PERIOD", "ACTIVE", "READ_ONLY", "SUSPENDED", "CANCELED"],
    GRACE_PERIOD: ["READ_ONLY", "ACTIVE", "SUSPENDED", "CANCELED"],
    READ_ONLY: ["ACTIVE", "SUSPENDED", "CANCELED"],
    SUSPENDED: ["ACTIVE", "READ_ONLY", "CANCELED"],
    CANCELED: []
  };
  if (!allowed[normFrom]?.includes(normTo)) {
    throw new DomainError(
      `Chuy\u1EC3n \u0111\u1ED5i tr\u1EA1ng th\xE1i g\xF3i d\u1ECBch v\u1EE5 kh\xF4ng h\u1EE3p l\u1EC7 t\u1EEB ${from} sang ${to}`,
      "INVALID_SUBSCRIPTION_STATUS_TRANSITION",
      400
    );
  }
}
function assertTenantCanWrite(entitlements) {
  if (!entitlements.canLogin) {
    throw new DomainError(
      "T\xE0i kho\u1EA3n doanh nghi\u1EC7p \u0111\xE3 b\u1ECB kh\xF3a ho\xE0n to\xE0n. Kh\xF4ng th\u1EC3 thao t\xE1c.",
      "TENANT_SUSPENDED",
      403
    );
  }
  if (!entitlements.canWrite) {
    throw new DomainError(
      "T\xE0i kho\u1EA3n \u0111ang \u1EDF ch\u1EBF \u0111\u1ED9 Ch\u1EC9 \u0111\u1ECDc (Read-Only) do h\u1EBFt h\u1EA1n d\xF9ng th\u1EED ho\u1EB7c g\xF3i d\u1ECBch v\u1EE5 t\u1EA1m d\u1EEBng. Vui l\xF2ng n\xE2ng c\u1EA5p g\xF3i \u0111\u1EC3 ti\u1EBFp t\u1EE5c ghi d\u1EEF li\u1EC7u.",
      "TENANT_READ_ONLY",
      403
    );
  }
}
function assertTenantCanExport(entitlements) {
  if (!entitlements.canExport) {
    throw new DomainError(
      "T\xE0i kho\u1EA3n kh\xF4ng \u0111\u01B0\u1EE3c ph\xE9p xu\u1EA5t d\u1EEF li\u1EC7u.",
      "EXPORT_FORBIDDEN",
      403
    );
  }
}
function assertSubscriptionOperational(status) {
  if (status === "SUSPENDED") {
    throw new DomainError(
      "G\xF3i d\u1ECBch v\u1EE5 \u0111ang t\u1EA1m ng\u01B0ng do qu\xE1 h\u1EA1n thanh to\xE1n. Vui l\xF2ng gia h\u1EA1n \u0111\u1EC3 ti\u1EBFp t\u1EE5c thao t\xE1c ghi d\u1EEF li\u1EC7u.",
      "SUBSCRIPTION_SUSPENDED",
      403
    );
  }
  if (status === "CANCELLED" || status === "CANCELED") {
    throw new DomainError(
      "G\xF3i d\u1ECBch v\u1EE5 \u0111\xE3 k\u1EBFt th\xFAc. Vui l\xF2ng \u0111\u0103ng k\xFD g\xF3i m\u1EDBi \u0111\u1EC3 ti\u1EBFp t\u1EE5c.",
      "SUBSCRIPTION_CANCELLED",
      403
    );
  }
  if (status === "READ_ONLY") {
    throw new DomainError(
      "G\xF3i d\u1ECBch v\u1EE5 \u0111\xE3 chuy\u1EC3n sang ch\u1EBF \u0111\u1ED9 Ch\u1EC9 \u0111\u1ECDc (Read-Only). Vui l\xF2ng n\xE2ng c\u1EA5p g\xF3i \u0111\u1EC3 ti\u1EBFp t\u1EE5c.",
      "TENANT_READ_ONLY",
      403
    );
  }
}
function assertWithinLimit(current, limit, metricLabel = "H\u1EA1n m\u1EE9c g\xF3i c\u01B0\u1EDBc") {
  if (limit != null && current >= limit) {
    throw new DomainError(
      `${metricLabel} \u0111\xE3 \u0111\u1EA1t t\u1ED1i \u0111a (${limit}). Vui l\xF2ng n\xE2ng c\u1EA5p g\xF3i d\u1ECBch v\u1EE5 \u0111\u1EC3 ti\u1EBFp t\u1EE5c m\u1EDF r\u1ED9ng.`,
      "USAGE_LIMIT_EXCEEDED",
      403
    );
  }
}
var assertWithinQuota = assertWithinLimit;
function assertFinancialAmount(value, field) {
  assertVnd(value, field);
  return value;
}
function negativeFinancialDelta(value) {
  return value === 0 ? 0 : -value;
}
function deriveFinancialEffects(input) {
  const total = assertFinancialAmount(input.totalAmount, "totalAmount");
  const cash = assertFinancialAmount(input.cashAmount ?? total, "cashAmount");
  if (["SALE", "PURCHASE", "SALES_REFUND", "PURCHASE_REFUND"].includes(input.type) && cash > total) {
    throw new DomainError(
      "S\u1ED1 ti\u1EC1n th\u1EF1c thu/th\u1EF1c tr\u1EA3 kh\xF4ng \u0111\u01B0\u1EE3c l\u1EDBn h\u01A1n gi\xE1 tr\u1ECB giao d\u1ECBch",
      "FINANCIAL_AMOUNT_EXCEEDS_TOTAL"
    );
  }
  switch (input.type) {
    case "SALE":
      return {
        cashDelta: cash,
        receivableDelta: total - cash,
        payableDelta: 0,
        revenueDelta: total
      };
    case "PURCHASE":
      return {
        cashDelta: negativeFinancialDelta(cash),
        receivableDelta: 0,
        payableDelta: total - cash,
        revenueDelta: 0
      };
    case "CUSTOMER_COLLECTION":
      return {
        cashDelta: total,
        receivableDelta: negativeFinancialDelta(total),
        payableDelta: 0,
        revenueDelta: 0
      };
    case "SUPPLIER_PAYMENT":
      return {
        cashDelta: negativeFinancialDelta(total),
        receivableDelta: 0,
        payableDelta: negativeFinancialDelta(total),
        revenueDelta: 0
      };
    case "SALES_REFUND":
      return {
        cashDelta: negativeFinancialDelta(cash),
        receivableDelta: negativeFinancialDelta(total - cash),
        payableDelta: 0,
        revenueDelta: negativeFinancialDelta(total)
      };
    case "PURCHASE_REFUND":
      return {
        cashDelta: cash,
        receivableDelta: 0,
        payableDelta: negativeFinancialDelta(total - cash),
        revenueDelta: 0
      };
    case "OPERATING_EXPENSE":
      return {
        cashDelta: negativeFinancialDelta(total),
        receivableDelta: 0,
        payableDelta: 0,
        revenueDelta: 0
      };
    case "OTHER_INCOME":
      return {
        cashDelta: total,
        receivableDelta: 0,
        payableDelta: 0,
        revenueDelta: total
      };
    case "CUSTOMER_DEPOSIT":
      return {
        cashDelta: total,
        receivableDelta: 0,
        payableDelta: 0,
        revenueDelta: 0
      };
    case "SUPPLIER_ADVANCE":
      return {
        cashDelta: negativeFinancialDelta(total),
        receivableDelta: 0,
        payableDelta: 0,
        revenueDelta: 0
      };
  }
}
function moneyDirectionFromCashDelta(cashDelta) {
  if (!Number.isSafeInteger(cashDelta)) {
    throw new DomainError("Bi\u1EBFn \u0111\u1ED9ng ti\u1EC1n kh\xF4ng h\u1EE3p l\u1EC7", "INVALID_MONEY_DELTA");
  }
  if (cashDelta > 0) return "IN";
  if (cashDelta < 0) return "OUT";
  return "NONE";
}
export {
  DomainError,
  allocateFefo,
  assertSubscriptionOperational,
  assertTenantCanExport,
  assertTenantCanWrite,
  assertVnd,
  assertWithinLimit,
  assertWithinQuota,
  baseUnitCost,
  calculateCashChange,
  calculateGrossMargin,
  calculateGrossProfit,
  calculateNetSales,
  calculateReturnAmount,
  calculateSaleCogs,
  calculateSellableStock,
  calculateWeightedAverageCost,
  categorizeExpiry,
  convertToBaseQuantity,
  deriveFinancialEffects,
  ensureStockAvailable,
  isDateExpired,
  lineTotal,
  moneyDirectionFromCashDelta,
  normalizeQuantity,
  reconcileClosing,
  roundQuantity,
  toBusinessDateString,
  unitPriceFromBase,
  validateSubscriptionStatusTransition,
  validateTenantStatusTransition,
  weightedAverageCost
};
